import { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { submitContact } from "../lib/api";
import { SERVICES } from "../lib/services";

export default function ContactPage() {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill name and email.");
      return;
    }
    try {
      setBusy(true);
      const payload = { ...form, source: "contact" };
      if (!payload.service) delete payload.service;
      await submitContact(payload);
      toast.success("Message sent", {
        description: "An advisor will reach out within one business day.",
      });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Submission failed", { description: "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32">
      <section className="pb-12">
        <div className="container-myc max-w-4xl">
          <p className="eyebrow">Contact</p>
          <h1 className="h-display-1 text-[#2A0A0E] mt-6">
            Let's start a{" "}
            <span className="italic font-normal text-[#7A1F26]">
              private conversation.
            </span>
          </h1>
          <p className="text-slate-600/65 mt-7 text-lg leading-relaxed max-w-2xl">
            Share a few details and a senior advisor will reach out within one
            business day. Everything is confidential.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-myc grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="card-myc"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-600/50 mb-1.5 block">Name</label>
                  <Input
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] text-[#2A0A0E] rounded-none h-11"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600/50 mb-1.5 block">Phone</label>
                  <Input
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 98xxxxxxxx"
                    className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] text-[#2A0A0E] rounded-none h-11"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs text-slate-600/50 mb-1.5 block">Email</label>
                <Input
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] text-[#2A0A0E] rounded-none h-11"
                />
              </div>
              <div className="mt-4">
                <label className="text-xs text-slate-600/50 mb-1.5 block">
                  Service of interest
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((s) => ({ ...s, service: v }))}
                >
                  <SelectTrigger
                    data-testid="contact-service-select"
                    className="bg-[#FAF5EB] border-slate-200/10 text-[#2A0A0E] rounded-none h-11"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FAF5EB] border-slate-200/10 text-[#2A0A0E]">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.slug} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="General Advisory">
                      General Advisory
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4">
                <label className="text-xs text-slate-600/50 mb-1.5 block">Message</label>
                <Textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us a bit about your goals or question."
                  rows={5}
                  className="bg-[#FAF5EB] border-slate-200/10 focus:border-[#7A1F26] text-[#2A0A0E] rounded-none"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit-button"
                className="btn-gold mt-6 disabled:opacity-60"
              >
                {busy ? "Sending..." : "Send Message"}
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-[11px] text-slate-600/40 mt-4">
                Your information remains confidential. We never share contact
                details with third parties.
              </p>
            </form>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="card-myc">
              <p className="eyebrow">Office</p>
              <p className="text-[#2A0A0E] mt-4 font-medium">Moneyyard Capital</p>
              <p className="text-slate-600/70 leading-relaxed mt-3">
                C-20, Enam Sambhav, G Block,
                <br />
                Bandra Kurla Complex, Bandra (East),
                <br />
                Mumbai — 400051, India.
              </p>
            </div>
            <a
              href="tel:+919892759155"
              data-testid="contact-page-phone"
              className="card-myc flex items-center gap-5 group"
            >
              <Phone className="w-5 h-5 text-[#7A1F26]" />
              <div>
                <p className="text-xs text-slate-600/50 tracking-widest uppercase">Phone</p>
                <p className="text-[#2A0A0E] font-medium group-hover:text-[#7A1F26] transition-colors">
                  +91 98927 59155
                </p>
              </div>
            </a>
            <a
              href="mailto:connect@moneyyardcapital.com"
              data-testid="contact-page-email"
              className="card-myc flex items-center gap-5 group"
            >
              <Mail className="w-5 h-5 text-[#7A1F26]" />
              <div>
                <p className="text-xs text-slate-600/50 tracking-widest uppercase">Email</p>
                <p className="text-[#2A0A0E] font-medium group-hover:text-[#7A1F26] transition-colors">
                  connect@moneyyardcapital.com
                </p>
              </div>
            </a>
            <div className="card-myc flex items-center gap-5">
              <MapPin className="w-5 h-5 text-[#7A1F26]" />
              <div>
                <p className="text-xs text-slate-600/50 tracking-widest uppercase">Hours</p>
                <p className="text-[#2A0A0E] font-medium">Mon – Fri · 10:00 – 19:00 IST</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
